/* eslint-disable camelcase */
import React from 'react'
import { css, StyleSheet } from 'aphrodite';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import Popper from '@material-ui/core/Popper';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { BaseEntityPage } from './BaseEntityPage';
import { AppTheme, AppStyles } from '../../styles/AppTheme';
import { CommonTheme } from '../../styles/Styles';
import { Helper } from '../helpers/Helper';
import { ApiHelper } from '../helpers/ApiHelper';
import { Controller } from '../Controller';
import { AppHelper } from '../../helpers/AppHelper';
import { CdpHelper } from '../../helpers/CdpHelper';
import { AppConfig } from '../../AppConfig';
import TableCard from './tables/TableCard';
import { PageDialog } from './PageDialog';
import { DynamicFormDialog } from './DynamicFormDialog';
import { AppButton } from './AppButton';
import { SearchBar } from './SearchBar';

function populateSettingsConfigValues(settingsConfig, orgId) {
  if (settingsConfig && settingsConfig.settings && typeof settingsConfig.settings === 'object') {
    const allSettings = CdpHelper.getAllSettings(orgId) || {};
    Object.values(settingsConfig.settings).forEach(setting => {
      setting.value = setting.defaultValue !== undefined ? setting.defaultValue : '';

      const fetchedRaw = allSettings[setting.key] !== undefined ? allSettings[setting.key] : '';
      const fetchedTrimmed = fetchedRaw != null ? String(fetchedRaw).trim() : '';

      if (fetchedTrimmed) {
        setting.value = fetchedTrimmed;
      }
    });
  }
}

function getSettingValueField(setting) {
  const fields = setting.fields || []
  const baseKey = String(setting.key || '').replace(/_ROLE_PSEUDO$/, '')
  return fields.find((f) => f && f.key === setting.key)
    || fields.find((f) => f && f.key === baseKey)
    || fields.find((f) => f && f.type === setting.type)
    || fields[0]
    || null
}

function resolveOptionLabel(options, key) {
  const normalizedKey = String(key || '').trim()
  if (!normalizedKey) {
    return ''
  }
  if (!Array.isArray(options)) {
    return normalizedKey
  }
  const match = options.find((o) => o && String(o.key) === normalizedKey)
  return match && match.label != null ? String(match.label) : normalizedKey
}

function getSettingDisplayValue(setting) {
  if (setting.value == null) {
    return ''
  }
  const rawValue = String(setting.value)
  if (!rawValue) {
    return ''
  }
  const field = getSettingValueField(setting)
  const options = field && Array.isArray(field.options) ? field.options : []
  if (setting.type === 'check_box') {
    return parseSettingCheckBoxValue(rawValue)
      .map((key) => resolveOptionLabel(options, key))
      .join(', ')
  }
  if (setting.type === 'radio') {
    return resolveOptionLabel(options, rawValue)
  }
  return rawValue
}

function isSettingUrl(value) {
  if (typeof value !== 'string') {
    return false
  }
  const urlRegex = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/i;
  return urlRegex.test(value.trim());
}

function getListFieldFromSetting(setting) {
  return (setting.fields || []).find((f) => f.type === 'list')
}

function getListRowFieldsFromSetting(setting) {
  const listField = getListFieldFromSetting(setting)
  return (listField && listField.listConfig && listField.listConfig.rowFields) || []
}

function getUniqueRowKeysFromFields(rowFields) {
  const seen = new Set()
  const keys = []
  ;(rowFields || []).forEach((f) => {
    if (f && f.key && !seen.has(f.key)) {
      seen.add(f.key)
      keys.push(f.key)
    }
  })
  return keys
}

function getFieldTypeFromRowFields(rowFields, key) {
  const field = (rowFields || []).find((f) => f.key === key)
  return field ? field.type : null
}

function isEditableSettingType(type) {
  return type === 'number' || type === 'string' || type === 'check_box' || type === 'list' || type === 'radio'
}

function parseSettingCheckBoxValue(csvValue) {
  if (csvValue == null || csvValue === '') {
    return []
  }
  return String(csvValue)
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean)
}

function serializeSettingCheckBoxValue(keys) {
  if (!Array.isArray(keys)) {
    return ''
  }
  return keys
    .map((item) => {
      if (item && typeof item === 'object' && item.key != null) {
        return String(item.key).trim()
      }
      return String(item).trim()
    })
    .filter(Boolean)
    .join(',')
}

function buildRoleOverrideDisplaySetting(setting, roleStr, roleOptions) {
  const baseKey = setting.key.replace(/_ROLE_PSEUDO$/, '');
  const fakeSetting = JSON.parse(JSON.stringify(setting));
  fakeSetting.key = baseKey;
  fakeSetting.value = roleStr || '';
  fakeSetting.type = 'check_box';
  fakeSetting.fields = [{
    key: baseKey,
    type: 'check_box',
    label: setting.title,
    multiSelect: true,
    options: roleOptions,
    v_required: { value: true, err: 'Select at least one role' },
    placement: 'left',
  }];
  return fakeSetting;
}

function normalizeConfigDefaultOverrideValue(value, settingType) {
  if (value == null) return null
  if (settingType === 'boolean') {
    if (value === true || value === 'enabled' || value === 'show') return 'enabled'
    if (value === false || value === 'disabled' || value === 'hide') return 'disabled'
  }
  return String(value)
}

function getConfigDefaultAtLevel(settingDef, level, target) {
  if (!settingDef || !settingDef.defaultValueOverrides || !target) return null
  const levelMap = settingDef.defaultValueOverrides[level]
  if (!levelMap || typeof levelMap !== 'object') return null
  const normalizedTarget = level === 'platform' ? String(target).toLowerCase() : String(target)
  let raw = levelMap[target]
  if (raw == null && normalizedTarget !== target) {
    raw = levelMap[normalizedTarget]
  }
  if (raw == null) {
    const matchKey = Object.keys(levelMap).find((k) => String(k).toLowerCase() === normalizedTarget.toLowerCase())
    if (matchKey != null) raw = levelMap[matchKey]
  }
  if (raw == null) return null
  return normalizeConfigDefaultOverrideValue(raw, settingDef.type)
}

function getConfigDefaultOverrideRows(settingDef) {
  if (!settingDef || !settingDef.defaultValueOverrides) return []
  const baseKey = settingDef.key
  const rows = []
  const { defaultValueOverrides } = settingDef

  if (defaultValueOverrides.platform && typeof defaultValueOverrides.platform === 'object') {
    Object.keys(defaultValueOverrides.platform).forEach((plat) => {
      const target = String(plat).toUpperCase() === 'DESKTOP' ? 'DESKTOP' : 'MOBILE'
      const value = getConfigDefaultAtLevel(settingDef, 'platform', plat)
      if (value != null) {
        rows.push({
          key: `${baseKey}__config_default__platform__${target}`,
          level: 'Platform (Default)',
          target,
          value,
          isConfigDefault: true,
        })
      }
    })
  }

  if (defaultValueOverrides.team && typeof defaultValueOverrides.team === 'object') {
    Object.keys(defaultValueOverrides.team).forEach((teamId) => {
      const value = getConfigDefaultAtLevel(settingDef, 'team', teamId)
      if (value != null) {
        rows.push({
          key: `${baseKey}__config_default__team__${teamId}`,
          level: 'Team (Default)',
          target: teamId,
          value,
          isConfigDefault: true,
        })
      }
    })
  }

  if (defaultValueOverrides.user && typeof defaultValueOverrides.user === 'object') {
    Object.keys(defaultValueOverrides.user).forEach((userId) => {
      const value = getConfigDefaultAtLevel(settingDef, 'user', userId)
      if (value != null) {
        rows.push({
          key: `${baseKey}__config_default__user__${userId}`,
          level: 'User (Default)',
          target: userId,
          value,
          isConfigDefault: true,
        })
      }
    })
  }

  return rows
}

function applyOverrideValueFieldRequired(field, baseSetting) {
  if (!field) return;
  const title = baseSetting.title || 'Value';
  const requiredErr = `${title} is required`;

  if (field.type === 'list') {
    field.v_required = { value: true, err: requiredErr };
    const cfg = field.listConfig || {};
    field.listConfig = {
      ...cfg,
      minItems: Math.max(cfg.minItems || 0, 1),
    };
    return;
  }

  field.v_required = { value: true, err: requiredErr };
  if (field.type === 'check_box') {
    field.multiSelect = true;
  }
}

function isListRowEmpty(row, rowFields) {
  return !(rowFields || []).some((rf) => {
    if (!rf || !rf.key) {
      return false
    }
    const cell = row && row[rf.key]
    if (getFieldTypeFromRowFields(rowFields, rf.key) === 'check_box') {
      return Array.isArray(cell) && cell.length > 0
    }
    return scalarListCellHasValue(cell)
  })
}

function validateListRows(rowFields, rows, { requireAtLeastOneRow = false, settingTitle = '' } = {}) {
  const safeRows = Array.isArray(rows) ? rows : []
  const nonEmptyRows = safeRows.filter((row) => !isListRowEmpty(row, rowFields))

  if (requireAtLeastOneRow && nonEmptyRows.length === 0) {
    return settingTitle ? `${settingTitle} is required` : 'At least one row is required'
  }

  for (const row of safeRows) {
    if (isListRowEmpty(row, rowFields)) {
      continue
    }
    for (const rf of rowFields) {
      if (!rf.v_required || !rf.v_required.value) {
        continue
      }
      const cell = row[rf.key]
      if (getFieldTypeFromRowFields(rowFields, rf.key) === 'check_box') {
        if (!Array.isArray(cell) || cell.length === 0) {
          return rf.v_required.err || (rf.label ? `${rf.label} is required` : `${settingTitle} is required`)
        }
      } else if (cell == null || String(cell).trim() === '') {
        return rf.v_required.err || (rf.label ? `${rf.label} is required` : `${settingTitle} is required`)
      }
    }
  }
  return null
}

function validateOverrideValueForm(baseSetting, formData) {
  const key = baseSetting.key;

  if (formData.overrideLevel === 'Role') {
    const roleVal = formData[`${key}_ROLE_VAL`];
    if (!Array.isArray(roleVal) || roleVal.length === 0) {
      return 'Select at least one role';
    }
    return null;
  }

  const value = formData[key];

  if (baseSetting.type === 'check_box') {
    if (!Array.isArray(value) || value.length === 0) {
      return `${baseSetting.title} is required`;
    }
    return null;
  }

  if (baseSetting.type === 'list') {
    const rows = Array.isArray(value) ? value : [];
    if (rows.length === 0) return `${baseSetting.title} is required`;
    const rowFields = getListRowFieldsFromSetting(baseSetting);
    return validateListRows(rowFields, rows, {
      requireAtLeastOneRow: true,
      settingTitle: baseSetting.title,
    });
  }

  if (value == null || String(value).trim() === '') {
    return `${baseSetting.title} is required`;
  }
  return null;
}

function parseListToken(token) {
  const trimmed = String(token || '').trim()
  if (!trimmed) {
    return null
  }
  if (trimmed.startsWith('+')) {
    const key = trimmed.slice(1).trim()
    return key ? { key, hasLabel: true } : null
  }
  return { key: trimmed, hasLabel: false }
}

function emptyListRow(rowFields) {
  const uniqueKeys = getUniqueRowKeysFromFields(rowFields)
  const row = {}
  uniqueKeys.forEach((key) => {
    row[key] = getFieldTypeFromRowFields(rowFields, key) === 'check_box' ? [] : ''
  })
  return row
}

function scalarListCellHasValue(val) {
  if (Array.isArray(val)) {
    return val.length > 0 && String(val[0] || '').trim().length > 0
  }
  return String(val || '').trim().length > 0
}

function rowHasListKeys(row, rowFields, listSerializer) {
  const keys = getUniqueRowKeysFromFields(rowFields)
  if (listSerializer === 'keyValuePairs') {
    return keys.some((key) => {
      const val = row[key]
      if (getFieldTypeFromRowFields(rowFields, key) === 'check_box') {
        return Array.isArray(val) && val.length > 0
      }
      return scalarListCellHasValue(val)
    })
  }
  return keys.some((key) => {
    const val = row[key]
    if (getFieldTypeFromRowFields(rowFields, key) === 'check_box') {
      return Array.isArray(val) && val.length > 0
    }
    return scalarListCellHasValue(val)
  })
}

function stringToFormState(rawString, rowFields, listSerializer) {
  const keys = getUniqueRowKeysFromFields(rowFields)
  const labelKeys = []
  const labelKeySet = new Set()
  const rows = []

  const rowSeparator = (listSerializer === 'equalsCommaPairs' || listSerializer === 'colonCommaPairs') ? ',' : '|'
  const segments = String(rawString != null ? rawString : '')
    .split(rowSeparator)
    .map((s) => s.trim())

  segments.forEach((segment, index) => {
    if (!segment) {
      return
    }

    const row = emptyListRow(rowFields)

    if (listSerializer === 'equalsCommaPairs') {
      const eqIdx = segment.indexOf('=')
      if (eqIdx !== -1) {
        row[keys[0]] = segment.slice(0, eqIdx).trim()
        row[keys[1]] = segment.slice(eqIdx + 1).trim()
      } else {
        row[keys[0]] = segment.trim()
      }
    } else if (listSerializer === 'colonPairs' || listSerializer === 'colonCommaPairs') {
      const colonIdx = segment.indexOf(':')
      if (colonIdx !== -1 && keys.length >= 2) {
        const leftRaw = segment.slice(0, colonIdx)
        const rightRaw = segment.slice(colonIdx + 1)
        const leftKey = keys[0]
        const rightKey = keys[1]

        if (getFieldTypeFromRowFields(rowFields, leftKey) === 'check_box') {
          row[leftKey] = leftRaw
            ? leftRaw.split(',').map((t) => t.trim()).filter(Boolean)
            : []
        } else {
          row[leftKey] = leftRaw ? leftRaw.trim() : ''
        }

        if (getFieldTypeFromRowFields(rowFields, rightKey) === 'check_box') {
          row[rightKey] = rightRaw
            ? rightRaw.split(',').map((t) => t.trim()).filter(Boolean)
            : []
        } else {
          row[rightKey] = rightRaw ? rightRaw.trim() : ''
        }
      } else if (keys.length > 0) {
        row[keys[0]] = segment.trim()
      }
    } else if (listSerializer === 'keyValuePairs') {
      let parsedExplicitly = false
      segment.split(';').forEach((part) => {
        const trimmed = part.trim()
        if (!trimmed) {
          return
        }
        const colonIdx = trimmed.indexOf(':')
        if (colonIdx === -1) {
          return
        }
        const fieldKey = trimmed.slice(0, colonIdx).trim()
        const rawValue = trimmed.slice(colonIdx + 1)
        if (!fieldKey) {
          return
        }
        if (!keys.includes(fieldKey)) {
          return
        }
        parsedExplicitly = true
        if (getFieldTypeFromRowFields(rowFields, fieldKey) === 'check_box') {
          row[fieldKey] = rawValue
            ? rawValue.split(',').map((t) => t.trim()).filter(Boolean)
            : []
        } else {
          row[fieldKey] = rawValue ? rawValue.trim() : ''
        }
      })
      if (!parsedExplicitly && keys.length >= 2) {
        const colonIdx = segment.indexOf(':')
        if (colonIdx !== -1) {
          const leftRaw = segment.slice(0, colonIdx)
          const rightRaw = segment.slice(colonIdx + 1)
          const leftKey = keys[0]
          const rightKey = keys[1]
          if (getFieldTypeFromRowFields(rowFields, leftKey) === 'check_box') {
            row[leftKey] = leftRaw
              ? leftRaw.split(',').map((t) => t.trim()).filter(Boolean)
              : []
          } else {
            row[leftKey] = leftRaw ? leftRaw.trim() : ''
          }
          if (getFieldTypeFromRowFields(rowFields, rightKey) === 'check_box') {
            row[rightKey] = rightRaw
              ? rightRaw.split(',').map((t) => t.trim()).filter(Boolean)
              : []
          } else {
            row[rightKey] = rightRaw ? rightRaw.trim() : ''
          }
        }
      }
    } else if (segment.includes(';') && keys.length > 0) {
      const parts = segment.split(';').map((p) => p.trim())
      while (parts.length < keys.length) {
        parts.push('')
      }
      keys.forEach((col, i) => {
        parts[i].split(',').forEach((token) => {
          const parsed = parseListToken(token)
          if (!parsed) {
            return
          }
          row[col].push(parsed.key)
          if (parsed.hasLabel && !labelKeySet.has(parsed.key)) {
            labelKeySet.add(parsed.key)
            labelKeys.push(parsed.key)
          }
        })
      })
    } else if (keys.length > 0) {
      segment.split(',').forEach((token, tokenIndex) => {
        const parsed = parseListToken(token)
        if (!parsed) {
          return
        }
        const targetCol = keys[tokenIndex]
        if (!targetCol) {
          return
        }

        const fieldType = getFieldTypeFromRowFields(rowFields, targetCol)
        if (fieldType === 'check_box') {
          row[targetCol].push(parsed.key)
        } else {
          row[targetCol] = parsed.key
        }

        if (parsed.hasLabel && !labelKeySet.has(parsed.key)) {
          labelKeySet.add(parsed.key)
          labelKeys.push(parsed.key)
        }
      })
    }

    if (!rowHasListKeys(row, rowFields, listSerializer)) {
      return
    }

    rows.push({
      id: `lr_import_${index}_${Math.random().toString(36).slice(2, 9)}`,
      ...row,
    })
  })

  return { rows, labelKeys }
}

function formStateToString(rows, labelKeys, rowFields, listSerializer) {
  const cols = getUniqueRowKeysFromFields(rowFields)
  const safeRows = Array.isArray(rows) ? rows : []
  const labelSet = listSerializer === 'labeled'
    ? new Set(
      (Array.isArray(labelKeys) ? labelKeys : [])
        .map((k) => String(k).trim())
        .filter(Boolean)
    )
    : null

  const formatToken = (key) => {
    const clean = String(key).trim()
    if (!clean) {
      return null
    }
    if (listSerializer === 'labeled' && labelSet) {
      return labelSet.has(clean) ? `+${clean}` : clean
    }
    return clean
  }

  const rowSeparator = (listSerializer === 'equalsCommaPairs' || listSerializer === 'colonCommaPairs') ? ',' : ' | '
  return safeRows
    .map((row) => {
      if (listSerializer === 'equalsCommaPairs') {
        const left = String(row[cols[0]] || '').trim()
        const right = String(row[cols[1]] || '').trim()
        if (left && right) {
          return `${left}=${right}`
        }
        if (left) {
          return left
        }
        return ''
      }
      if (listSerializer === 'colonPairs' || listSerializer === 'colonCommaPairs') {
        const left = String(row[cols[0]] || '').trim()
        const right = String(row[cols[1]] || '').trim()
        if (left && right) {
          return `${left}: ${right}`
        }
        if (left) {
          return left
        }
        return ''
      }
      if (listSerializer === 'keyValuePairs') {
        const pairs = []
        cols.forEach((col) => {
          const val = row[col]
          const fieldType = getFieldTypeFromRowFields(rowFields, col)
          let formattedVal = ''
          if (fieldType === 'check_box') {
            const arr = Array.isArray(val) ? val : (val ? [val] : [])
            formattedVal = arr.map((item) => String(item).trim()).filter(Boolean).join(',')
          } else {
            const raw = Array.isArray(val) ? val[0] : val
            formattedVal = raw == null ? '' : String(raw).trim()
          }
          if (formattedVal) {
            pairs.push(`${col}:${formattedVal}`)
          }
        })
        return pairs.join('; ')
      }
      const tokens = []
      cols.forEach((col) => {
        const val = row[col]
        const fieldType = getFieldTypeFromRowFields(rowFields, col)
        if (fieldType === 'check_box') {
          (Array.isArray(val) ? val : []).forEach((key) => {
            const formatted = formatToken(key)
            if (formatted) {
              tokens.push(formatted)
            }
          })
        } else if (val != null && String(val).trim().length > 0) {
          const formatted = formatToken(String(val).trim())
          if (formatted) {
            tokens.push(formatted)
          }
        }
      })
      return tokens.join(', ')
    })
    .filter((line) => line.length > 0)
    .join(rowSeparator)
}

function OverridesDialogBody(props) {
  return <div>{props.children}</div>
}

export class Settings2 extends BaseEntityPage {
  constructor(props, options) {
    super(props)
    this.pageTitle = Helper.getString('settings')
    this.pageKey = 'settings'
    this.orgId = this.props.match.params.orgId || 'PRODUCT'
    const appUser = (Controller.get().userMgr() && Controller.get().userMgr().getAppUser()) || {};
    this.isStaff = this.isStaffEligibleForDevToggle();

    const search = (this.props.location && this.props.location.search) ||
      (typeof window !== 'undefined' && window.location.search) || '';
    const initialDev = this.isDevSearch(search);
    const initialCs = this.isCsSearch(search);
    const initialToggled = initialDev || initialCs;

    this.showInternal = initialToggled;
    this.showDeveloper = initialDev;
    this.emptyText = ' ' // space
    this.breadCrumbs = [
      { title: Helper.getString('home'), to: this.baseRoute() },
      { title: this.pageTitle },
    ]
    this.groupId = 'settings' + this.orgId
    this.crmGroupId = 'settingscrmcontactattrs' + this.orgId;
    this.crmAccountAttrsGroupId = 'settingscrmaccountattrs' + this.orgId;
    this.toFetch = [
      {
        key: 'crmcontactattrs',
        ms: AppConfig.CDP_MS.ENDPOINT,
        method: 'GET',
        paths: ['items', `${this.crmGroupId}-all`],
        queryParams: { orgId: this.orgId, groupId: this.crmGroupId }
      },
      {
        key: 'crmaccountattrs',
        ms: AppConfig.CDP_MS.ENDPOINT,
        method: 'GET',
        paths: ['items', `${this.crmAccountAttrsGroupId}-all`],
        queryParams: { orgId: this.orgId, groupId: this.crmAccountAttrsGroupId }
      },
      { key: 'usersORG', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['miscs', 'get-org-users2'], queryParams: { orgId: this.orgId, parentOrgId: this.orgId } },
      { key: 'suborgs', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['miscs', 'get-sub-orgs'], queryParams: { orgId: this.orgId, parentOrgId: this.orgId } }
    ];
    this.users = [];
    this.teams = [];
    this.noAdd = true
    this.apiData = {}
    this.editingSetting = null
    this.editingOverrideBaseSetting = null
    this.state = {
      ...this.state,
      searchText: '',
      devSettingsToggled: initialToggled,
    };
  }

  isStaffEligibleForDevToggle() {
    const appUser = (Controller.get().userMgr() && Controller.get().userMgr().getAppUser()) || {};
    return Helper.isSlixtaStaff(appUser.email);
  }

  isDevSearch(search = '') {
    if (!this.isStaffEligibleForDevToggle()) {
      return false;
    }
    const params = new URLSearchParams(search);
    return params.has('showdev') || params.has('showDev');
  }

  isCsSearch(search = '') {
    if (!this.isStaffEligibleForDevToggle()) {
      return false;
    }
    const params = new URLSearchParams(search);
    return params.has('showcs') || params.has('showCs') ||
      params.has('showinternal') || params.has('showInternal');
  }

  isDevOrCsSearchToggled(search = '') {
    return this.isDevSearch(search) || this.isCsSearch(search);
  }

  isDevOrCsToggled() {
    if (this.state && typeof this.state.devSettingsToggled === 'boolean') {
      return this.state.devSettingsToggled;
    }
    const search = (this.props.location && this.props.location.search) ||
      (typeof window !== 'undefined' && window.location.search) || '';
    return this.isDevOrCsSearchToggled(search);
  }

  componentDidUpdate(prevProps) {
    const prevSearch = prevProps && prevProps.location ? prevProps.location.search : '';
    const currentSearch = this.props.location ? this.props.location.search : '';
    if (prevSearch !== currentSearch) {
      const hasDev = this.isDevSearch(currentSearch);
      const hasCs = this.isCsSearch(currentSearch);
      const isToggled = hasDev || hasCs;
      this.showDeveloper = hasDev;
      this.showInternal = isToggled;
      if (this.state.devSettingsToggled !== isToggled) {
        this.setState({ devSettingsToggled: isToggled });
      }
    }
  }

  handleToggleDevSettings = (checked) => {
    const pathname = (this.props.location && this.props.location.pathname) ||
      (typeof window !== 'undefined' && window.location.pathname) || '';
    const search = (this.props.location && this.props.location.search) ||
      (typeof window !== 'undefined' && window.location.search) || '';
    const params = new URLSearchParams(search);

    params.delete('showdev');
    params.delete('showDev');
    params.delete('showcs');
    params.delete('showCs');
    params.delete('showinternal');
    params.delete('showInternal');

    let newSearch = '';
    if (checked) {
      const qs = params.toString();
      newSearch = qs ? `?${qs}&showcs` : '?showcs';
    } else {
      const qs = params.toString();
      newSearch = qs ? `?${qs}` : '';
    }

    const targetUrl = `${pathname}${newSearch}`;

    if (this.props.history && this.props.history.push) {
      this.props.history.push(targetUrl);
    } else if (this.props.history && this.props.history.replace) {
      this.props.history.replace(targetUrl);
    } else if (typeof window !== 'undefined' && window.history && window.history.pushState) {
      window.history.pushState({}, '', targetUrl);
    }

    if (checked) {
      this.showDeveloper = false;
      this.showInternal = true;
    } else {
      this.showDeveloper = false;
      this.showInternal = false;
    }

    this.isStaff = this.isStaffEligibleForDevToggle();
    this.setState({ devSettingsToggled: checked });
  }

  renderDevToggleSwitch() {
    if (!this.isStaffEligibleForDevToggle()) {
      return null;
    }

    const isToggled = this.isDevOrCsToggled();
    const label = 'Show All';
    const tooltipTitle = isToggled ? 'Hide all settings' : 'Show all settings';

    return (
      <div className={css(Styles.devToggleContainer)}>
        <Tooltip title={tooltipTitle} placement='top'>
          <FormControlLabel
            className={css(Styles.devToggleFormLabel)}
            classes={{ label: css(Styles.devToggleLabel) }}
            control={
              <Switch
                checked={isToggled}
                onChange={(e) => this.handleToggleDevSettings(e.target.checked)}
                color='primary'
              />
            }
            label={label}
          />
        </Tooltip>
      </div>
    );
  }

  onSearch = (searchText = '') => {
    this.setState({ searchText });
  }

  clearSearch = () => {
    this.setState({ searchText: '' });
  }

  loadSettingsData = async () => {
    const settingsConfig = JSON.parse(JSON.stringify(CdpHelper.resolveSettingsConfig(this.orgId)));

    if (!settingsConfig.settings) settingsConfig.settings = {};

    settingsConfig.settings.USER_TAGS = {
      key: 'USER_TAGS',
      category: 'crm',
      visibility: 'public',
      levels: [],
      section: 'contacts',
      title: 'Tags',
      description: 'Tags that can be assigned to CRM contacts',
      type: 'check_box',
      defaultValue: '',
      fields: [
        {
          key: 'USER_TAGS',
          type: 'check_box',
          label: 'User Tags',
          addOptions: true,
          addOptionsFormatter: 'tagformat',
          options: [],
          v_required: { value: false, err: 'Provide Tags' },
          placement: 'left'
        }
      ]
    };

    settingsConfig.settings.ACCOUNT_TAGS = {
      key: 'ACCOUNT_TAGS',
      category: 'crm',
      visibility: 'developer',
      levels: [],
      section: 'accounts',
      title: 'Account Tags',
      description: 'Tags that can be assigned to CRM accounts',
      type: 'check_box',
      defaultValue: '',
      fields: [
        {
          key: 'ACCOUNT_TAGS',
          type: 'check_box',
          label: 'Account Tags',
          addOptions: true,
          addOptionsFormatter: 'tagformat',
          options: [],
          v_required: { value: false, err: 'Provide Tags' },
          placement: 'left'
        }
      ]
    };

    await CdpHelper.enrichSettingsFieldOptions(settingsConfig, this.orgId, {
      staffUserId: Controller.get().getUserId(),
      staffUsers: this.users,
      crmContactAttrs: this.crmDataFull,
    });
    populateSettingsConfigValues(settingsConfig, this.orgId);

    if (settingsConfig.settings.USER_TAGS) {
      settingsConfig.settings.USER_TAGS.value = (this.crmDataFull && this.crmDataFull.tags) || '';
    }
    if (settingsConfig.settings.ACCOUNT_TAGS) {
      settingsConfig.settings.ACCOUNT_TAGS.value = (this.crmAccountDataFull && this.crmAccountDataFull.tags) || '';
      const accountTagOpts = Helper.extractAttrs(this.crmAccountDataFull, 'tags').map(t => ({ key: t, label: t }))
      if (settingsConfig.settings.ACCOUNT_TAGS.fields && settingsConfig.settings.ACCOUNT_TAGS.fields[0]) {
        settingsConfig.settings.ACCOUNT_TAGS.fields[0].options = accountTagOpts
      }
    }

    this.setState({ settings: settingsConfig });
  }

  async onFetchSuccess(results) {
    this.crmDataFull = this.getResult(results, 'crmcontactattrs') || (results && results[0]) || {};
    this.crmAccountDataFull = this.getResult(results, 'crmaccountattrs') || {};
    const usersRes = this.getResult(results, 'usersORG') || null;
    this.users = usersRes && usersRes.items ? usersRes.items : [];
    const teamsRes = this.getResult(results, 'suborgs') || null;
    this.teams = teamsRes && teamsRes.items ? teamsRes.items : [];
    await this.loadSettingsData();
  }
  getSettingValue(settingKey, defaultValue = '') {
    const settingsMap = this.state && this.state.settings && this.state.settings.settings
    if (!settingsMap || typeof settingsMap !== 'object') {
      return defaultValue
    }
    const setting = settingsMap[settingKey]
    return setting ? setting.value : defaultValue
  }

  applySettingValueToState(settingKey, newValue) {
    const envelope = this.state && this.state.settings
    const settingsMap = envelope && envelope.settings
    if (!settingsMap || typeof settingsMap !== 'object') {
      return
    }
    const setting = settingsMap[settingKey]
    if (!setting) return

    this.setState({
      settings: {
        ...envelope,
        settings: {
          ...settingsMap,
          [settingKey]: { ...setting, value: newValue },
        },
      },
    })
  }

  updateSetting = (settingKey, newValue) => {
    let prevVal = this.getSettingValue(settingKey, null);
    if (prevVal === null) {
      const allSettings = CdpHelper.getAllSettings(this.orgId) || {};
      prevVal = allSettings[settingKey] !== undefined ? allSettings[settingKey] : '';
    }

    return CdpHelper.setSetting({
      orgId: this.orgId,
      previousKey: settingKey,
      previousValue: prevVal,
      settingsKey: settingKey,
      settingsValue: newValue,
    }).then((res) => {
      if (res && res.success) {
        this.loadSettingsData();
      }
      return res;
    });
  }

  getSettingOverrides(baseKey) {
    const baseSetting = this.state.settings && this.state.settings.settings && this.state.settings.settings[baseKey];
    const overrides = baseSetting ? getConfigDefaultOverrideRows(baseSetting) : [];
    const allSettings = CdpHelper.getAllSettings(this.orgId) || {};
    Object.keys(allSettings).forEach(k => {
      if (!allSettings[k]) return;
      let level, target;
      if (k === `${baseKey}_MOBILE`) { level = 'Platform'; target = 'MOBILE'; }
      else if (k === `${baseKey}_DESKTOP`) { level = 'Platform'; target = 'DESKTOP'; }
      else if (k.startsWith(`${baseKey}:`)) {
        const id = k.split(':')[1];
        if (this.teams.some(t => t.id === id)) { level = 'Team'; target = id; }
        else { level = 'User'; target = id; }
      }
      if (level) overrides.push({ key: k, level, target, value: allSettings[k] });
    });
    if (baseSetting && baseSetting.type === 'boolean') {
      const roleStr = this.getRoleOverrideString(baseSetting);
      if (roleStr) {
        overrides.push({ key: `${baseKey}_ROLE_PSEUDO`, level: 'Role', target: '', value: roleStr });
      }
    }
    return overrides;
  }

  getRoleOverrideString(baseSetting) {
    if (!baseSetting || baseSetting.type !== 'boolean') return null;
    const val = baseSetting.value;
    if (val && val !== 'enabled' && val !== 'disabled') {
      return val;
    }
    return null;
  }

  getCanonicalSetting(settingOrKey) {
    const key = (typeof settingOrKey === 'string' ? settingOrKey : settingOrKey.key)
      .replace(/_ROLE_PSEUDO$/, '');
    const settingsMap = this.state.settings && this.state.settings.settings;
    return settingsMap && settingsMap[key] ? settingsMap[key] : null;
  }

  getRoleOverrideOptions(selectedRoleStr = '', setting = null) {
    const options = [
      { key: 'SUPERUSER', label: 'SUPERUSER' },
      { key: 'BLOGGER', label: 'BLOGGER' },
      { key: 'CRM', label: 'CRM' },
      { key: 'VIEWER', label: 'VIEWER' },
    ];
    const visibility = (setting && setting.visibility) || 'developer';
    const allowStaffRoles = visibility !== 'public' && (this.showInternal || this.showDeveloper) && this.isStaffEligibleForDevToggle();
    if (allowStaffRoles) {
      options.push({ key: 'SLIXTA', label: 'SLIXTA' });
      options.push({ key: 'PRODUCTSU', label: 'PRODUCTSU' });
    }
    parseSettingCheckBoxValue(selectedRoleStr).forEach((r) => {
      if ((r === 'SLIXTA' || r === 'PRODUCTSU') && !allowStaffRoles) return;
      if (!options.some((o) => o.key === r)) {
        options.push({ key: r, label: r });
      }
    });
    return options;
  }

  getUserOptions() {
    const seen = new Set();
    const options = [];
    this.users.forEach((u) => {
      if (!u.userId || seen.has(u.userId)) return;
      seen.add(u.userId);
      options.push({
        key: u.userId,
        label: `${u.givenname || ''} ${u.familyname || ''}`.trim() || u.email || u.userId,
      });
    });
    return options;
  }

  requestDeleteOverride = (overrideKey, label) => {
    this.setState({ pendingOverrideDelete: { key: overrideKey, label } });
  }

  handleOverrideDeleteSubmit = (options) => {
    const { caller } = options;
    const key = this.state.pendingOverrideDelete && this.state.pendingOverrideDelete.key;
    if (!key || !caller) {
      if (caller) caller.setState({ inProcess: false });
      return;
    }

    if (key.includes('__config_default__')) {
      caller.setState({ inProcess: false });
      this.setState({ pendingOverrideDelete: null });
      return;
    }

    if (key.endsWith('_ROLE_PSEUDO')) {
      const baseKey = key.replace('_ROLE_PSEUDO', '');
      const configSetting = this.getCanonicalSetting(baseKey);
      let revertValue = (configSetting && configSetting.defaultValue != null)
        ? configSetting.defaultValue
        : 'disabled';

      if (revertValue !== 'enabled' && revertValue !== 'disabled') {
        revertValue = 'disabled';
      }

      return this.updateSetting(baseKey, revertValue).then((res) => {
        if (res && res.success) {
          this.setState({ pendingOverrideDelete: null });
          caller.setState({ success: true, inProcess: false, message: '' });
        } else {
          caller.setState({
            success: false,
            inProcess: false,
            message: (res && res.error) || 'Failed to delete override',
          });
        }
      }).catch((err) => {
        caller.setState({
          success: false,
          inProcess: false,
          message: Helper.getErrorMsg(err),
        });
      });
    }

    return this.updateSetting(key, null).then((res) => {
      if (res && res.success) {
        this.setState({ pendingOverrideDelete: null });
        caller.setState({ success: true, inProcess: false, message: '' });
      } else {
        caller.setState({
          success: false,
          inProcess: false,
          message: (res && res.error) || 'Failed to delete override',
        });
      }
    }).catch((err) => {
      caller.setState({
        success: false,
        inProcess: false,
        message: Helper.getErrorMsg(err),
      });
    });
  }

  openAddOverrideEditor = (baseSettingArg) => {
    const baseSetting = this.getCanonicalSetting(baseSettingArg) || baseSettingArg;
    const overrideLevelOptions = this.getAddableOverrideLevelOptions(baseSetting);
    if (overrideLevelOptions.length === 0) return;

    this.editingOverrideBaseSetting = baseSetting;
    this.editingSetting = null;

    const userOptions = this.getUserOptions();
    const teamOptions = this.teams.map(t => ({ key: t.id, label: t.title }));

    const roleOptions = this.getRoleOverrideOptions('', baseSetting);

    const overrideFields = [
      { key: 'overrideLevel', type: 'select', label: 'Level', options: overrideLevelOptions, v_required: { value: true, err: 'Level is required' }, placement: 'left' },
      { key: 'overrideTargetPlatform', type: 'select', label: 'Target', options: [{ key: 'MOBILE', label: 'Mobile' }, { key: 'DESKTOP', label: 'Desktop' }], condition: { key: 'overrideLevel', value: 'Platform' }, v_required: { value: true, err: 'Platform is required' }, placement: 'right' },
      { key: 'overrideTargetTeam', type: 'select', label: 'Target', options: teamOptions, condition: { key: 'overrideLevel', value: 'Team' }, v_required: { value: true, err: 'Team is required' }, placement: 'right' },
      { key: 'overrideTargetUser', type: 'select', label: 'Target', options: userOptions, condition: { key: 'overrideLevel', value: 'User' }, v_required: { value: true, err: 'User is required' }, placement: 'right' },
      { key: `${baseSetting.key}_ROLE_VAL`, type: 'check_box', label: 'Target', condition: { key: 'overrideLevel', value: 'Role' }, options: roleOptions, v_required: { value: true, err: 'Select at least one role' }, placement: 'right' }
    ];

    let valueFields = JSON.parse(JSON.stringify(baseSetting.fields || []));
    if (baseSetting.type === 'boolean') {
      valueFields = [
        {
          key: baseSetting.key,
          type: 'select',
          label: baseSetting.title,
          condition: { key: 'overrideLevel', op: 'not', value: 'Role', values: ['Role'] },
          options: [{ key: 'enabled', label: 'Enabled' }, { key: 'disabled', label: 'Disabled' }],
          v_required: { value: true, err: 'Value is required' },
          placement: 'left',
          style: { marginTop: 24 }
        },
        {
          key: `${baseSetting.key}_ROLE_FIXED_VAL`,
          type: 'select',
          label: baseSetting.title,
          condition: { key: 'overrideLevel', value: 'Role' },
          defaultValue: 'enabled',
          options: [{ key: 'enabled', label: 'Enabled' }],
          v_required: { value: false, err: '' },
          placement: 'left',
          style: { marginTop: 24 },
          disabled: true
        }
      ];
    } else if (valueFields.length > 0) {
      const vf = valueFields[0];
      vf.placement = 'left';
      if (vf.type === 'list') {
        if (!vf.listConfig) vf.listConfig = {};
        vf.listConfig = { ...vf.listConfig, title: baseSetting.title };
      } else {
        vf.label = baseSetting.title;
      }
      applyOverrideValueFieldRequired(vf, baseSetting);
    }

    const fieldDef = { title: `Add Override: ${baseSetting.title}`, formType: 'edit', ctaButtonText: 'Save Override', steps: [{ fields: [...overrideFields, ...valueFields] }] };

    const currentFilterLevel = this.state.filterOverrideLevel || 'All';
    const currentFilterTarget = this.state.filterOverrideTarget || 'All';
    const formValues = {};

    if (currentFilterLevel !== 'All' && overrideLevelOptions.some(o => o.key === currentFilterLevel)) {
      formValues.overrideLevel = currentFilterLevel;
      if (currentFilterTarget !== 'All') {
        if (currentFilterLevel === 'Platform') formValues.overrideTargetPlatform = currentFilterTarget;
        else if (currentFilterLevel === 'Team') formValues.overrideTargetTeam = currentFilterTarget;
        else if (currentFilterLevel === 'User') formValues.overrideTargetUser = currentFilterTarget;
      }
    } else {
      formValues.overrideLevel = overrideLevelOptions.length > 0 ? overrideLevelOptions[0].key : '';
    }

    if (baseSetting.type === 'check_box' || baseSetting.type === 'list') formValues[baseSetting.key] = [];
    if (baseSetting.type === 'boolean') formValues[`${baseSetting.key}_ROLE_FIXED_VAL`] = 'enabled';

    super.openModalForEdit(formValues, fieldDef);
  }

  openSettingEditor = (setting) => {
    if (!setting || !setting.fields) {
      return
    }
    this.editingSetting = setting
    this.editingOverrideBaseSetting = null;
    const fieldDef = {
      title: setting.title,
      description: setting.description,
      formType: 'edit',
      ctaButtonText: 'Save',
      steps: [{ fields: setting.fields }],
    }
    let formValues
    if (setting.type === 'check_box') {
      formValues = { [setting.key]: parseSettingCheckBoxValue(setting.value) }
    } else if (setting.type === 'list') {
      const rowFields = getListRowFieldsFromSetting(setting)
      const { rows, labelKeys } = stringToFormState(
        setting.value,
        rowFields,
        setting.listSerializer
      )
      formValues = { [setting.key]: rows }
      if (setting.labelsFieldKey) {
        formValues[setting.labelsFieldKey] = labelKeys
      }
    } else {
      formValues = { [setting.key]: setting.value }
    }
    super.openModalForEdit(formValues, fieldDef)
  }

  customSubmit(options) {
    const { formData, caller } = options;
    const editingSetting = this.editingSetting || this.editingOverrideBaseSetting;
    if (!editingSetting) return;

    const baseKey = editingSetting.key;
    let finalKey = baseKey;

    if (this.editingOverrideBaseSetting) {
      const level = formData.overrideLevel;
      if (level === 'Role') finalKey = `${baseKey}_ROLE_PSEUDO`;
      const addableLevels = this.getAddableOverrideLevelOptions(editingSetting).map(o => o.key);
      if (level && !addableLevels.includes(level)) {
        if (caller) {
          caller.setState({
            inProcess: false,
            success: false,
            message: `${level} overrides are not supported for this setting.`,
          });
        }
        return;
      }
      if (level === 'Platform') finalKey = `${baseKey}_${formData.overrideTargetPlatform}`;
      else if (level === 'Team') finalKey = `${baseKey}:${formData.overrideTargetTeam}`;
      else if (level === 'User') finalKey = `${baseKey}:${formData.overrideTargetUser}`;

      if (!finalKey || finalKey === baseKey || finalKey.endsWith('_undefined') || finalKey.endsWith(':undefined')) {
        if (caller) caller.setState({ inProcess: false });
        return;
      }

      const valueErr = validateOverrideValueForm(editingSetting, formData);
      if (valueErr) {
        if (caller) {
          caller.setState({ inProcess: false, success: false, message: valueErr });
        }
        return;
      }

      const existingOverrides = this.getSettingOverrides(baseKey);
      if (level === 'Role' && this.getRoleOverrideString(editingSetting)) {
        if (caller) {
          caller.setState({
            inProcess: false,
            success: false,
            message: 'An override for this target already exists. Please delete it first or edit it from the table.',
          });
        }
        return;
      }
      if (existingOverrides.some(o => o.key === finalKey)) {
        if (caller) {
          caller.setState({
            inProcess: false,
            success: false,
            message: 'An override for this target already exists. Please delete it first or edit it from the table.',
          });
        }
        return;
      }
    }

    let newValue = '';
    if (finalKey === `${baseKey}_ROLE_PSEUDO`) {
      finalKey = baseKey;
      newValue = serializeSettingCheckBoxValue(formData[`${baseKey}_ROLE_VAL`]);
    } else {
      newValue = formData[baseKey];
      if (editingSetting.type === 'check_box') {
        newValue = serializeSettingCheckBoxValue(newValue);
      } else if (editingSetting.type === 'list') {
        const rowFields = getListRowFieldsFromSetting(editingSetting);
        newValue = formStateToString(newValue, formData[editingSetting.labelsFieldKey], rowFields, editingSetting.listSerializer);
      } else {
        if (newValue == null) newValue = '';
        newValue = String(newValue).trim();
      }
    }

    if (finalKey === 'USER_TAGS') {
      const payload = { ...this.crmDataFull, tags: newValue };
      ApiHelper.callAwait({
        method: 'PUT',
        jsonBody: payload,
        endPoint: ApiHelper.makeUrlPath2({
          ms: AppConfig.CDP_MS.ENDPOINT,
          paths: ['items', `${this.crmGroupId}-all`],
          queryParams: { orgId: this.orgId, groupId: this.crmGroupId }
        }),
      }).then(() => {
        this.editingSetting = null;
        this.editingOverrideBaseSetting = null;
        this.setState({ modalOpen: false });
        this.fetchItems();
      }).catch(err => {
        if (caller) {
          caller.setState({
            success: false,
            inProcess: false,
            message: Helper.getErrorMsg(err),
          });
        }
      });
      return;
    }

    if (finalKey === 'ACCOUNT_TAGS') {
      const payload = { ...this.crmAccountDataFull, tags: newValue };
      ApiHelper.callAwait({
        method: 'PUT',
        jsonBody: payload,
        endPoint: ApiHelper.makeUrlPath2({
          ms: AppConfig.CDP_MS.ENDPOINT,
          paths: ['items', `${this.crmAccountAttrsGroupId}-all`],
          queryParams: { orgId: this.orgId, groupId: this.crmAccountAttrsGroupId }
        }),
      }).then(() => {
        this.editingSetting = null;
        this.editingOverrideBaseSetting = null;
        this.setState({ modalOpen: false });
        this.fetchItems();
      }).catch(err => {
        if (caller) {
          caller.setState({
            success: false,
            inProcess: false,
            message: Helper.getErrorMsg(err),
          });
        }
      });
      return;
    }

    this.updateSetting(finalKey, newValue).then((res) => {
      if (res && res.success) {
        this.editingSetting = null;
        this.editingOverrideBaseSetting = null;
        this.setState({ modalOpen: false });
      } else if (caller) {
        caller.setState({
          success: false,
          inProcess: false,
          message: (res && res.error) || 'Failed to update setting',
        });
      }
    }).catch((err) => {
      if (caller) {
        caller.setState({
          success: false,
          inProcess: false,
          message: Helper.getErrorMsg(err),
        });
      }
    });
  }

  async onFormFieldOptionAdd({ formField, value }) {
    const activeSetting = this.editingSetting || this.editingOverrideBaseSetting;
    if (!activeSetting || !formField || value == null) {
      return (formField && formField.options) || [];
    }
    if (!formField.options) {
      formField.options = [];
    }
    const trimmed = String(value).trim();
    if (!trimmed) {
      return formField.options;
    }
    if (!formField.options.some((o) => o.key === trimmed)) {
      formField.options.push({ key: trimmed, label: trimmed });
    }
    if (this.editingOverrideBaseSetting && this.editingOverrideBaseSetting.fields) {
      const canonical = this.editingOverrideBaseSetting.fields.find((f) => f.key === formField.key);
      if (canonical && canonical !== formField) {
        if (!canonical.options) canonical.options = [];
        if (!canonical.options.some((o) => o.key === trimmed)) {
          canonical.options.push({ key: trimmed, label: trimmed });
        }
      }
    }
    return formField.options;
  }

  getGroupedSettings() {
    const envelope = this.state.settings;
    const settingsMap = envelope && envelope.settings;
    const categoriesMap = envelope && envelope.categories;
    const sectionsMap = envelope && envelope.sections;

    if (!settingsMap || typeof settingsMap !== 'object' || !categoriesMap || !sectionsMap) {
      return [];
    }

    const categoryMap = new Map();
    Object.values(categoriesMap).forEach(cat => {
      if (!cat || !cat.key) return;
      categoryMap.set(cat.key, {
        key: cat.key,
        title: cat.title,
        description: cat.description,
        sectionMap: new Map(),
      });
    });

    Object.values(settingsMap).forEach((setting) => {
      if (!setting || !setting.category || !setting.section || setting.responsiveParent) {
        return;
      }

      const visibility = setting.visibility || 'developer';
      let isVisible = false;

      if (visibility === 'public') {
        isVisible = true;
      } else if (visibility === 'internal') {
        if (this.isStaffEligibleForDevToggle() && (this.showInternal || this.showDeveloper)) {
          isVisible = true;
        }
      } else if (visibility === 'developer') {
        if (this.isStaffEligibleForDevToggle() && this.showDeveloper) {
          isVisible = true;
        }
      }

      if (!isVisible && setting.category !== 'site-settings') {
        return;
      }

      const catKey = setting.category;
      if (!categoryMap.has(catKey)) {
        const catDef = categoriesMap[catKey];
        categoryMap.set(catKey, {
          key: catKey,
          title: (catDef && catDef.title) || catKey,
          description: (catDef && catDef.description) || '',
          sectionMap: new Map(),
        });
      }

      const category = categoryMap.get(catKey);
      const secKey = setting.section;
      const secDef = sectionsMap[secKey] || { key: secKey, title: secKey };

      if (!category.sectionMap.has(secKey)) {
        category.sectionMap.set(secKey, {
          key: secDef.key,
          title: secDef.title,
          settings: [],
        });
      }

      category.sectionMap.get(secKey).settings.push(setting);
    });

    const sectionOrder = Object.keys(sectionsMap);

    return Array.from(categoryMap.values()).map((category) => {
      const activeSections = Array.from(category.sectionMap.values()).filter(sec => sec.settings && sec.settings.length > 0);

      activeSections.sort((a, b) => {
        const idxA = sectionOrder.indexOf(a.key);
        const idxB = sectionOrder.indexOf(b.key);
        if (idxA === -1 && idxB === -1) return 0;
        if (idxA === -1) return 1;
        if (idxB === -1) return -1;
        return idxA - idxB;
      });

      return {
        key: category.key,
        title: category.title,
        description: category.description,
        sections: activeSections,
      };
    }).filter(category => category.sections.length > 0 || category.key === 'site-settings');
  }

  render() {
    const { categoryKey } = this.props.match.params;
    const currentUrl = this.props.match.url.replace(/\/$/, '');
    const search = this.props.location.search || '';
    const settingsBaseRoute = categoryKey ? currentUrl.replace(`/${categoryKey}`, '') : currentUrl;

    this.breadCrumbs = [
      { title: Helper.getString('home'), to: this.baseRoute() },
      { title: this.pageTitle, to: categoryKey ? `${settingsBaseRoute}${search}` : undefined },
    ];

    const settingsConfigSource = this.state.settings || CdpHelper.resolveSettingsConfig(this.orgId);
    if (categoryKey && settingsConfigSource.categories && settingsConfigSource.categories[categoryKey]) {
      this.breadCrumbs.push({ title: settingsConfigSource.categories[categoryKey].title });
    }

    return super.render();
  }

  renderMasterCategoryGrid() {
    const groupedCategories = this.getGroupedSettings().filter(c => c.key !== 'site-settings');
    const currentUrl = this.props.match.url.replace(/\/$/, '');
    const search = this.props.location.search || '';

    return (
      <div className={css(Styles.masterGrid)}>
        {groupedCategories.map(cat => (
          <div
            key={cat.key}
            className={css(Styles.categoryCardLink)}
            onClick={() => this.props.history.push(`${currentUrl}/${cat.key}${search}`)}
          >
            <h3 className={css(Styles.categoryCardTitle)}>{cat.title}</h3>
            {cat.description && (
              <p className={css(Styles.categoryCardDesc)}>{cat.description}</p>
            )}
          </div>
        ))}
        {this.isStaffEligibleForDevToggle() && this.showDeveloper && (
          <div
            className={css(Styles.categoryCardLink)}
            onClick={() => {
              window.location.href = `${window.location.origin}${this.baseRoute()}/site-settings`;
            }}
          >
            <h3 className={css(Styles.categoryCardTitle)}>Site Settings</h3>
            <p className={css(Styles.categoryCardDesc)}>Manage global site configurations and branding.</p>
          </div>
        )}
      </div>
    )
  }

  renderAboveTable() {
    if (!this.state.settings) {
      return null
    }
    return (
      <div className={css(Styles.containerNarrow)}>
        {this.renderSettingsContent()}
      </div>
    );
  }

  getAddableOverrideLevelOptions(baseSetting) {
    if (!baseSetting) return [];
    const configuredLevels = Array.isArray(baseSetting.levels) ? baseSetting.levels.map(l => l.toLowerCase()) : [];
    if (configuredLevels.length === 0) return [];

    const options = [];
    if (configuredLevels.includes('platform')) options.push({ key: 'Platform', label: 'Platform' });
    if (configuredLevels.includes('team') && this.teams && this.teams.length > 0) options.push({ key: 'Team', label: 'Team' });
    if (configuredLevels.includes('user') && this.users && this.users.length > 0) options.push({ key: 'User', label: 'User' });
    if (baseSetting.type === 'boolean' && configuredLevels.includes('role')) {
      options.push({ key: 'Role', label: 'Role' });
    }
    return options;
  }

  getOverrideLevelFilterOptions(baseSetting) {
    const options = [{ key: 'All', label: 'All' }];
    const existingOverrides = baseSetting ? this.getSettingOverrides(baseSetting.key) : [];
    const configuredLevels = baseSetting && Array.isArray(baseSetting.levels) ? baseSetting.levels.map(l => l.toLowerCase()) : [];
    const configOverrides = baseSetting && baseSetting.defaultValueOverrides;
    const hasConfigPlatform = configOverrides && configOverrides.platform && Object.keys(configOverrides.platform).length > 0;
    const hasConfigTeam = configOverrides && configOverrides.team && Object.keys(configOverrides.team).length > 0;
    const hasConfigUser = configOverrides && configOverrides.user && Object.keys(configOverrides.user).length > 0;
    const hasStoredPlatform = existingOverrides.some(o => o.level === 'Platform');
    const hasStoredTeam = existingOverrides.some(o => o.level === 'Team');
    const hasStoredUser = existingOverrides.some(o => o.level === 'User');
    const hasStoredRole = existingOverrides.some(o => o.level === 'Role');
    const hasTeams = this.teams && this.teams.length > 0;
    const hasUsers = this.users && this.users.length > 0;

    if (hasConfigPlatform) {
      options.push({ key: 'Platform (Default)', label: 'Platform (Default)' });
    }
    if (configuredLevels.includes('platform') || hasStoredPlatform) {
      options.push({ key: 'Platform', label: 'Platform' });
    }
    if (hasConfigTeam && hasTeams) {
      options.push({ key: 'Team (Default)', label: 'Team (Default)' });
    }
    if ((configuredLevels.includes('team') || hasStoredTeam) && hasTeams) {
      options.push({ key: 'Team', label: 'Team' });
    }
    if (hasConfigUser && hasUsers) {
      options.push({ key: 'User (Default)', label: 'User (Default)' });
    }
    if ((configuredLevels.includes('user') || hasStoredUser) && hasUsers) {
      options.push({ key: 'User', label: 'User' });
    }
    if (baseSetting && baseSetting.type === 'boolean' && (configuredLevels.includes('role') || hasStoredRole)) {
      options.push({ key: 'Role', label: 'Role' });
    }

    return options;
  }

  getOverrideTargetFilterOptions(filterLevel, baseSetting) {
    const options = [{ key: 'All', label: 'All' }];
    if (!filterLevel) return options;

    if (filterLevel === 'Platform (Default)' && baseSetting && baseSetting.defaultValueOverrides && baseSetting.defaultValueOverrides.platform) {
      Object.keys(baseSetting.defaultValueOverrides.platform).forEach((plat) => {
        const key = String(plat).toUpperCase() === 'DESKTOP' ? 'DESKTOP' : 'MOBILE';
        options.push({ key, label: key === 'MOBILE' ? 'Mobile' : 'Desktop' });
      });
    } else if (filterLevel === 'Platform') {
      options.push({ key: 'MOBILE', label: 'Mobile' }, { key: 'DESKTOP', label: 'Desktop' });
    } else if (filterLevel === 'Team (Default)' && baseSetting && baseSetting.defaultValueOverrides && baseSetting.defaultValueOverrides.team) {
      Object.keys(baseSetting.defaultValueOverrides.team).forEach((teamId) => {
        const team = this.teams.find(t => t.id === teamId);
        options.push({ key: teamId, label: team ? team.title : teamId });
      });
    } else if (filterLevel === 'Team') {
      this.teams.forEach((t) => options.push({ key: t.id, label: t.title }));
    } else if (filterLevel === 'User (Default)' && baseSetting && baseSetting.defaultValueOverrides && baseSetting.defaultValueOverrides.user) {
      Object.keys(baseSetting.defaultValueOverrides.user).forEach((userId) => {
        const user = this.users.find(u => u.userId === userId);
        const label = user
          ? `${user.givenname || ''} ${user.familyname || ''}`.trim() || user.email || user.userId
          : userId;
        options.push({ key: userId, label });
      });
    } else if (filterLevel === 'User') {
      this.getUserOptions().forEach((o) => options.push(o));
    }
    return options;
  }

  renderOverridesFilterAutocomplete = (label, options, selectedKey, onChange, disabled = false, searchable = false) => {
    const selected = options.find((o) => o.key === selectedKey) || options[0];
    const canOpen = !disabled && options.length > 1;
    return (
      <div className={css(Styles.formControlContainer)} style={searchable ? { minWidth: 220 } : undefined}>
        <p className={css(Styles.filterLabel)}>{label}</p>
        <FormControl className={css(Styles.formControlMainFilters)} variant='outlined'>
          <Autocomplete
            value={selected}
            options={options}
            onChange={(e, val) => onChange(searchable ? (val ? val.key : 'All') : (val && val.key))}
            getOptionLabel={(option) => option.label || ''}
            getOptionSelected={(option, value) => option.key === value.key}
            disableClearable
            disabled={searchable ? disabled : (disabled || !canOpen)}
            {...(!searchable && {
              forcePopupIcon: canOpen,
              popupIcon: canOpen ? undefined : null,
            })}
            PopperComponent={(props) => (
              <Popper {...props} style={{ ...props.style, zIndex: 13001 }} />
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant='standard'
                className={css(Styles.formControlFilters)}
                placeholder={searchable ? 'Search Target...' : undefined}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  ...(searchable ? {} : { readOnly: true }),
                  classes: {
                    root: css([Styles.textInputStyles, Styles.autoCompleteInputRoot]),
                    input: css(Styles.formInputStyle, Styles.textInput),
                  },
                }}
              />
            )}
          />
        </FormControl>
      </div>
    );
  }

  renderOverridesModal() {
    if (!this.state.overridesModalOpen || !this.state.activeOverrideSetting) return null;
    const baseSetting = this.getCanonicalSetting(this.state.activeOverrideSetting);
    if (!baseSetting) return null;
    let overrides = this.getSettingOverrides(baseSetting.key);

    if (this.state.filterOverrideLevel && this.state.filterOverrideLevel !== 'All') {
      overrides = overrides.filter(ov => ov.level === this.state.filterOverrideLevel);
    }
    if (this.state.filterOverrideTarget && this.state.filterOverrideTarget !== 'All') {
      overrides = overrides.filter(ov => ov.target === this.state.filterOverrideTarget);
    }

    const tableData = overrides.map(ov => {
      let targetLabel = ov.target;
      if (ov.level === 'Team' || ov.level === 'Team (Default)') {
        const team = this.teams.find(t => t.id === ov.target);
        if (team) targetLabel = team.title;
      } else if (ov.level === 'User' || ov.level === 'User (Default)') {
        const user = this.users.find(u => u.userId === ov.target);
        if (user) targetLabel = `${user.givenname || ''} ${user.familyname || ''}`.trim() || user.email || user.userId;
      } else if (ov.level === 'Platform' || ov.level === 'Platform (Default)') {
        if (ov.target === 'MOBILE') targetLabel = 'Mobile';
        else if (ov.target === 'DESKTOP') targetLabel = 'Desktop';
      }

      const fakeSetting = JSON.parse(JSON.stringify(baseSetting));
      fakeSetting.key = ov.key;
      fakeSetting.value = ov.value;
      if (fakeSetting.fields) {
        fakeSetting.fields.forEach(f => { if (f.key === baseSetting.key) f.key = ov.key; });
      }
      if (fakeSetting.labelsFieldKey && fakeSetting.labelsFieldKey.startsWith(baseSetting.key)) {
        fakeSetting.labelsFieldKey = fakeSetting.labelsFieldKey.replace(baseSetting.key, ov.key);
      }

      return { id: ov.key, level: ov.level, target: targetLabel, settingObj: fakeSetting, isConfigDefault: !!ov.isConfigDefault };
    });

    const filterOverrideLevel = this.state.filterOverrideLevel || 'All';
    const filterOverrideTarget = this.state.filterOverrideTarget || 'All';
    const levelOptions = this.getOverrideLevelFilterOptions(baseSetting);
    const targetOptions = this.getOverrideTargetFilterOptions(
      filterOverrideLevel === 'All' ? null : filterOverrideLevel,
      baseSetting
    );
    const showTargetFilter = filterOverrideLevel !== 'All' && filterOverrideLevel !== 'Role';
    const addableLevelOptions = this.getAddableOverrideLevelOptions(baseSetting);

    const modalContent = (
      <div className={css(Styles.overridesModalContent)}>
        <div className={css(Styles.overridesFilterBar)}>
          <div className={css(Styles.overridesFilterGroup)}>
            {this.renderOverridesFilterAutocomplete('Level', levelOptions, filterOverrideLevel, (key) => {
              this.setState({ filterOverrideLevel: key, filterOverrideTarget: 'All' });
            })}
            {showTargetFilter && this.renderOverridesFilterAutocomplete(
              'Target',
              targetOptions,
              filterOverrideTarget,
              (key) => this.setState({ filterOverrideTarget: key }),
              false,
              true
            )}
          </div>
          {addableLevelOptions.length > 0 && (
            <AppButton buttonText='Add Override' icon='add' onClick={() => this.openAddOverrideEditor(baseSetting)} />
          )}
        </div>
        <div className={css(Styles.overridesTableContainer)}>
          <TableCard
            isModalTable={true}
            compactEmpty={true}
            disableTooltip={true}
            item={{
              data: (() => {
                const tableHeadersMap = {
                  Level: (row) => row.level,
                  Target: (row) => {
                    if (row.level === 'Role') {
                      return this.renderSettingControl(row.settingObj, true);
                    }
                    return row.target;
                  },
                  Value: (row) => {
                    if (row.level === 'Role') {
                      return <Switch checked={true} color='primary' disabled={true} />;
                    }
                    if (row.isConfigDefault && row.settingObj.type === 'boolean') {
                      return <Switch checked={row.settingObj.value === 'enabled'} color='primary' disabled={true} />;
                    }
                    if (row.isConfigDefault) {
                      return (
                        <span className={css(Styles.settingValueText)} title={getSettingDisplayValue(row.settingObj)}>
                          {getSettingDisplayValue(row.settingObj)}
                        </span>
                      );
                    }
                    return this.renderSettingControl(row.settingObj, true);
                  },
                  Actions: (row) => (
                    row.isConfigDefault ? null : (
                      <Tooltip key='del' title='Delete Override'>
                        <IconButton size='small' onClick={() => this.requestDeleteOverride(row.id, `${row.level} - ${row.target}`)}>
                          <i className='material-icons-outlined' style={{ color: 'red', fontSize: 20 }}>delete</i>
                        </IconButton>
                      </Tooltip>
                    )
                  ),
                };
                const headers = ['Level', 'Target', 'Value', 'Actions'];
                return {
                  headers,
                  textColumnIndices: [0, 1, 2],
                  rows: tableData.map(row => headers.map(h => tableHeadersMap[h](row))),
                };
              })(),
            }}
          />
        </div>
      </div>
    );

    const pendingOverrideDelete = this.state.pendingOverrideDelete;

    return (
      <>
        <PageDialog
          isOpen={this.state.overridesModalOpen}
          title={`Overrides: ${baseSetting.title}`}
          PageComponent={OverridesDialogBody}
          PageComponentProps={{ children: modalContent }}
          fullWidth={true}
          maxWidth='md'
          fullWidthDialog={false}
          onClosed={() => this.setState({
            overridesModalOpen: false,
            activeOverrideSetting: null,
            pendingOverrideDelete: null,
            filterOverrideLevel: 'All',
            filterOverrideTarget: 'All',
          })}
        />
        <DynamicFormDialog
          key={pendingOverrideDelete ? 'delete-' + pendingOverrideDelete.key : 'override-delete-idle'}
          isOpen={!!pendingOverrideDelete}
          onClosed={() => this.setState({ pendingOverrideDelete: null })}
          nestedDialog={true}
          fullWidthDialog={false}
          maxWidth='xs'
          formDefinition={{
            formType: 'delete',
            skipDeleteConfirmInput: true,
            title: pendingOverrideDelete ? `Delete ${pendingOverrideDelete.label}` : 'Delete',
            ctaButtonText: Helper.getString('delete'),
            steps: [{ fields: [] }],
          }}
          customSubmit={this.handleOverrideDeleteSubmit}
        />
      </>
    );
  }

  renderSettingsContent() {
    const { categoryKey } = this.props.match.params;
    const search = (this.state.searchText || '').trim().toLowerCase();

    const searchBarComponent = (
      <div className={css(Styles.searchBarToolbar)}>
        <div className={css(Styles.searchBarContainer)} onSubmit={(e) => e.preventDefault()}>
          <SearchBar
            autoFocus={false}
            autoRefresh={true}
            clearSearchLabel='Clear'
            debounceDuration={1}
            hideFiltersContainer={true}
            hint='Search settings...'
            onClearSearch={this.clearSearch}
            onSearch={this.onSearch}
            searchButtonLabel='Search'
            searchText={this.state.searchText}
          />
        </div>
        {this.renderDevToggleSwitch()}
      </div>
    );

    if (!categoryKey) {
      if (!search) {
        return (
          <>
            {searchBarComponent}
            {this.renderMasterCategoryGrid()}
            {this.renderOverridesModal()}
          </>
        );
      }

      const allCategories = this.getGroupedSettings().filter(c => c.key !== 'site-settings');
      const filteredCategories = allCategories.map(cat => {
        const catMatches = cat.title.toLowerCase().includes(search) ||
          (cat.description && cat.description.toLowerCase().includes(search));

        const filteredSections = cat.sections.map(section => {
          const secMatches = section.title.toLowerCase().includes(search);
          let filteredSettings = section.settings;

          if (!catMatches && !secMatches) {
            filteredSettings = section.settings.filter(setting =>
              setting.title.toLowerCase().includes(search) ||
              (setting.description && setting.description.toLowerCase().includes(search))
            );
          }

          return { ...section, settings: filteredSettings };
        }).filter(section => section.settings.length > 0);

        return { ...cat, sections: filteredSections };
      }).filter(cat => cat.sections.length > 0);

      const showSiteSettings = this.isStaffEligibleForDevToggle() && this.showDeveloper &&
        ('site settings'.includes(search) || 'manage global site configurations'.includes(search));

      return (
        <>
          {searchBarComponent}
          {filteredCategories.length > 0 || showSiteSettings ? (
            <div className={css(Styles.categoryBlock)}>
              {filteredCategories.map(cat => this.renderCategory(cat))}

              {showSiteSettings && (
                <div className={css(Styles.masterGrid)} style={{ marginTop: 16 }}>
                  <div
                    className={css(Styles.categoryCardLink)}
                    onClick={() => {
                      window.location.href = `${window.location.origin}${this.baseRoute()}/site-settings`;
                    }}
                  >
                    <h3 className={css(Styles.categoryCardTitle)}>Site Settings</h3>
                    <p className={css(Styles.categoryCardDesc)}>Manage global site configurations and branding.</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className={css(Styles.emptyStateContainer)}>
              <p className={css(Styles.emptyStateText)}>No matching settings found.</p>
            </div>
          )}
          {this.renderOverridesModal()}
        </>
      );
    }

    const categories = this.getGroupedSettings();
    const targetCategory = categories.find(c => c.key === categoryKey);

    if (!targetCategory) {
      return (
        <div className={css(Styles.emptyStateContainer)}>
          <i className={css(Styles.emptyStateIcon) + ' material-icons-outlined'}>info</i>
          <p className={css(Styles.emptyStateText)}>Settings Not Available</p>
        </div>
      );
    }

    let filteredCategory = targetCategory;
    if (search) {
      const filteredSections = targetCategory.sections.map(section => {
        const sectionMatches = section.title.toLowerCase().includes(search);
        let filteredSettings = section.settings;

        if (!sectionMatches) {
          filteredSettings = section.settings.filter(setting =>
            setting.title.toLowerCase().includes(search) ||
            (setting.description && setting.description.toLowerCase().includes(search))
          );
        }

        return { ...section, settings: filteredSettings };
      }).filter(section => section.settings.length > 0);

      filteredCategory = { ...targetCategory, sections: filteredSections };
    }

    return (
      <>
        <div className={css(Styles.categoryBlock)}>
          <h2 className={css(Styles.categoryTitle)}>{filteredCategory.title}</h2>
          {searchBarComponent}

          {filteredCategory.sections.length > 0 ? (
            filteredCategory.sections.map((section) =>
              this.renderSectionCard(section, filteredCategory.key)
            )
          ) : (
            <div className={css(Styles.emptyStateContainer)}>
              <p className={css(Styles.emptyStateText)}>No matching settings found.</p>
            </div>
          )}
        </div>
        {this.renderOverridesModal()}
      </>
    );
  }

  renderCategory(category) {
    return (
      <div key={category.key} className={css(Styles.categoryBlock)}>
        <h2 className={css(Styles.categoryTitle)}>{category.title}</h2>
        {category.sections.map((section) => this.renderSectionCard(section, category.key))}
      </div>
    )
  }

  renderSectionCard(section, categoryKey) {
    const settings = section.settings || []
    return (
      <div key={`${categoryKey}-${section.key}`} className={css(Styles.sectionCard)}>
        <div className={css(Styles.sectionHeader)}>
          <h3 className={css(Styles.sectionTitle)}>{section.title}</h3>
        </div>
        <div className={css(Styles.sectionContent)}>
          {settings.map((setting, index) =>
            this.renderSettingRow(setting, index < settings.length - 1)
          )}
        </div>
      </div>
    )
  }

  renderVisibilityBadge(setting) {
    const visibility = setting.visibility || 'developer'
    if (visibility === 'internal') {
      return <span className={css(Styles.visibilityBadge, Styles.visibilityBadgeCs)}>CS</span>
    }
    if (visibility === 'developer') {
      return <span className={css(Styles.visibilityBadge, Styles.visibilityBadgeDeveloper)}>DEV</span>
    }
    return null
  }

  renderSettingRow(setting, showDivider) {
    const rowStyles = showDivider
      ? [Styles.settingRow, Styles.settingRowDivider]
      : Styles.settingRow
    return (
      <div key={setting.key} className={css(rowStyles)}>
        <div className={css(Styles.settingText)}>
          <div className={css(Styles.settingTitleRow)}>
            <p className={css(Styles.settingTitle)}>{setting.title}</p>
            {this.renderVisibilityBadge(setting)}
          </div>
          {setting.description &&
            <p className={css(Styles.settingDescription)}>{setting.description}</p>
          }
        </div>
        <div className={css(Styles.settingControl)}>
          {this.renderSettingControl(setting)}
        </div>
      </div>
    )
  }

  renderSettingOverrideButton(setting) {
    if (!setting) {
      return null;
    }
    const canonical = this.getCanonicalSetting(setting) || setting;
    const hasOverrides = this.getSettingOverrides(canonical.key).length > 0;
    const configuredLevels = Array.isArray(canonical.levels) ? canonical.levels : [];
    if (!hasOverrides && configuredLevels.length === 0) {
      return null;
    }
    return (
      <Tooltip title='Overrides' placement='top-start'>
        <span className={css(AppStyles.entityTableLinkIcon)} style={{ position: 'relative' }}>
          <i
            className='material-icons-outlined'
            style={{ color: '#888' }}
            onClick={() => this.setState({
              overridesModalOpen: true,
              activeOverrideSetting: canonical,
              filterOverrideLevel: 'All',
              filterOverrideTarget: 'All',
            })}
          >
            tune
          </i>
          {hasOverrides && <span className={css(Styles.overrideBadgeDot)} />}
        </span>
      </Tooltip>
    )
  }

  renderSettingEditButton(setting) {
    return (
      <Tooltip title='Edit' placement='top-start'>
        <span className={css(AppStyles.entityTableLinkIcon)}>
          <i
            className='material-icons-outlined'
            onClick={() => this.openSettingEditor(setting)}
          >
            edit
          </i>
        </span>
      </Tooltip>
    )
  }

  renderSettingValueBoxContent(setting, displayValue) {
    if (!displayValue) {
      return <span className={css(Styles.settingValueText, Styles.settingValueEmpty)}></span>
    }
    if (setting.type === 'string' && isSettingUrl(displayValue)) {
      const trimmed = displayValue.trim()
      const href = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
      return (
        <a
          href={href}
          target='_blank'
          rel='noopener noreferrer'
          className={css(Styles.settingValueLink)}
          title={displayValue}
          onClick={(e) => e.stopPropagation()}
        >
          {displayValue}
        </a>
      )
    }
    return (
      <span className={css(Styles.settingValueText)} title={displayValue}>
        {displayValue}
      </span>
    )
  }

  renderSettingValueInBox(setting) {
    if (['list'].includes(setting.type)) {
      return null
    }
    const displayValue = getSettingDisplayValue(setting)
    const boxProps = {
      className: css(Styles.settingValueBox),
    }
    return (
      <div {...boxProps}>
        <div className={css(Styles.settingValueBoxInner)}>
          {this.renderSettingValueBoxContent(setting, displayValue)}
        </div>
      </div>
    )
  }

  renderEditableSettingActions(setting, isOverrideTableRow = false) {
    return (
      <div className={css(isOverrideTableRow ? Styles.overrideTableSettingActions : Styles.settingActions)}>
        {this.renderSettingValueInBox(setting)}
        {this.renderSettingEditButton(setting)}
        {!isOverrideTableRow && this.renderSettingOverrideButton(setting)}
      </div>
    )
  }

  renderSettingControl(setting, isOverrideTableRow = false) {
    if (setting.type === 'boolean') {
      if (isOverrideTableRow && setting.key.endsWith('_ROLE_PSEUDO')) {
        return this.renderEditableSettingActions(
          buildRoleOverrideDisplaySetting(setting, setting.value, this.getRoleOverrideOptions(setting.value, this.getCanonicalSetting(setting) || setting)),
          true
        );
      }

      const canonical = this.getCanonicalSetting(setting);
      const roleStr = !isOverrideTableRow && canonical && this.getRoleOverrideString(canonical);

      const switchCtrl = (
        <Switch
          color='primary'
          checked={setting.value === 'enabled'}
          disabled={!!roleStr}
          onChange={(e) =>
            this.updateSetting(setting.key, e.target.checked ? 'enabled' : 'disabled')
          }
        />
      );
      if (isOverrideTableRow) {
        return switchCtrl;
      }
      return (
        <div className={css(Styles.settingActions)}>
          {switchCtrl}
          {this.renderSettingOverrideButton(setting)}
        </div>
      )
    }
    if (isEditableSettingType(setting.type)) {
      return this.renderEditableSettingActions(setting, isOverrideTableRow)
    }
    return null
  }
}

const Styles = StyleSheet.create({
  searchBarToolbar: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
  },
  searchBarContainer: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
  },
  devToggleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    alignSelf: 'center',
  },
  devToggleFormLabel: {
    margin: 0,
    marginRight: 0,
    marginLeft: 0,
    userSelect: 'none',
    display: 'inline-flex',
    alignItems: 'center',
  },
  devToggleLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: '#374151',
    whiteSpace: 'nowrap',
  },
  containerNarrow: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 10,
    maxWidth: AppTheme.viewPort,
    paddingBottom: 30,
    width: '100%',
  },
  categoryBlock: {
    width: '100%',
    marginBottom: 16,
  },
  categoryTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    margin: '0 0 12px 0',
    color: '#222222',
    textAlign: 'left',
  },
  sectionCard: {
    background: 'white',
    borderRadius: AppTheme.borderRadius,
    border: `1px solid ${AppTheme.borderColor}`,
    marginBottom: 12,
    width: '100%',
  },
  sectionHeader: {
    borderBottom: '1px solid lightgray',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    margin: 0,
    textAlign: 'left',
    padding: AppTheme.pagePadding * 4,
  },
  sectionContent: {
    padding: AppTheme.pagePadding * 4,
  },
  settingRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 24,
    width: '100%',
    paddingBottom: 20,
  },
  settingRowDivider: {
    borderBottom: `1px solid ${AppTheme.borderColor}`,
    marginBottom: 20,
    paddingBottom: 20,
  },
  settingText: {
    flexShrink: 0,
    maxWidth: '35%',
    minWidth: 0,
  },
  settingTitleRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  settingTitle: {
    margin: 0,
    fontSize: 16,
    color: '#222222',
  },
  visibilityBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 700,
    lineHeight: '14px',
    padding: '2px 6px',
    letterSpacing: 0.4,
    flexShrink: 0,
  },
  visibilityBadgeCs: {
    backgroundColor: '#FEF3C7',
    color: '#B45309',
  },
  visibilityBadgeDeveloper: {
    backgroundColor: '#EDE9FE',
    color: '#6D28D9',
  },
  settingDescription: {
    fontSize: 14,
    color: 'grey',
    marginTop: 2,
    marginBottom: 0,
  },
  settingControl: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  settingActions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    flex: 1,
    minWidth: 0,
    maxWidth: 600
  },
  overrideTableSettingActions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 8,
    width: '100%',
    minWidth: 0,
  },
  settingValueBox: {
    width: 'fit-content',
    minWidth: 140,
    maxWidth: '100%',
    height: 38,
    minHeight: 38,
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${AppTheme.borderColor}`,
    borderRadius: 8,
    backgroundColor: '#f5f5f5',
    padding: '0 12px',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  settingValueBoxClickable: {
    cursor: 'pointer',
  },
  settingValuePlaceholder: {
    fontSize: 16,
    color: '#9ca3af',
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0,
    textAlign: 'left',
  },
  settingValueBoxInner: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
    height: '100%',
    overflow: 'hidden',
  },
  settingValueText: {
    fontSize: 16,
    color: '#222222',
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0,
    textAlign: 'left',
  },
  settingValueEmpty: {
    color: 'grey',
  },
  settingValueLink: {
    color: AppTheme.primaryColor,
    textDecoration: 'none',
    fontSize: 16,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
    width: '100%',
    minWidth: 0,
    flex: 1,
  },
  masterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 20,
    width: '100%',
    paddingTop: 10,
  },
  categoryCardLink: {
    background: 'white',
    borderRadius: AppTheme.borderRadius,
    border: `1px solid ${AppTheme.borderColor}`,
    padding: '24px 20px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    ':hover': {
      border: `1px solid ${AppTheme.primaryColor}`,
    }
  },
  categoryCardTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#222222',
    margin: '0 0 8px 0',
  },
  categoryCardDesc: {
    fontSize: 14,
    color: 'grey',
    margin: 0,
    lineHeight: '1.4',
  },
  emptyStateContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '40vh',
    width: '100%',
  },
  emptyStateIcon: {
    fontSize: 48,
    color: '#ccc',
    marginBottom: 16,
  },
  emptyStateText: {
    fontSize: 18,
    color: '#666',
    fontWeight: 500,
    margin: 0,
  },
  overridesTableContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    paddingLeft: 0,
    paddingRight: 0,
    minHeight: 0,
  },
  overridesModalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  addOverrideButtonContainer: {
    marginBottom: 2,
    marginTop: 2,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  formControlContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 5,
  },
  filterLabel: {
    fontSize: 14,
    color: CommonTheme.subTextColor,
    margin: 0,
    display: 'inline-block',
  },
  formControlMainFilters: {
    width: 240,
    '@media (min-width: 1024px) and (max-width: 1280px)': {
      width: 200,
      alignSelf: 'center',
    },
    '@media (min-width: 768px) and (max-width: 1024px)': {
      width: '60%',
      alignSelf: 'center',
    },
    '@media (max-width: 767px)': {
      width: '60%',
      alignSelf: 'center',
    },
  },
  formControlFilters: {
    borderRadius: AppTheme.borderRadius,
    border: '1px solid #E5E5E5',
    overflow: 'hidden',
  },
  textInputStyles: {
    padding: '11px 20px',
    flex: 1,
    textAlign: 'left',
    color: '#222222',
    fontSize: 16,
  },
  autoCompleteInputRoot: {
    paddingRight: 48,
    '@media (max-width: 440px)': {
      paddingRight: 18,
    },
  },
  formInputStyle: {
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  textInput: {
    padding: '4px 0px',
    '@media (max-width: 440px)': {
      fontSize: 12,
    },
  },
  overridesFilterBar: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    width: '100%',
    marginTop: 8,
    marginBottom: 16,
    gap: 10,
    position: 'relative',
    zIndex: 1,
  },
  overridesFilterGroup: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  overrideBadgeDot: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 8,
    height: 8,
    backgroundColor: AppTheme.primaryColor || '#2563EB',
    borderRadius: '50%',
    border: '1px solid white',
    pointerEvents: 'none',
  },
})
